import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { NewspaperIcon } from '@heroicons/react/24/outline';

export default function PressContentContainer() {
  const [content, setContent] = useState('');
  const [status, setStatus] = useState<'Published' | 'Draft'>('Draft');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [itemId, setItemId] = useState<string | null>(null);

  useEffect(() => {
    fetchGuidelines();
  }, []);

  const fetchGuidelines = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('content_items')
        .select('*')
        .eq('title', 'Media Guidelines')
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setItemId(data.id);
        setStatus(data.status || 'Draft');
        let parsed = data.content;
        if (typeof parsed === 'string') {
          try {
            parsed = JSON.parse(parsed);
          } catch {
            // use as-is
          }
        }
        if (typeof parsed === 'object' && parsed !== null) {
          setContent(parsed.markdown || parsed.text || JSON.stringify(parsed, null, 2));
        } else {
          setContent(parsed || '');
        }
      }
    } catch (err) {
      console.error('Error fetching media guidelines:', err);
      toast.error('Failed to load media guidelines');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        title: 'Media Guidelines',
        type: 'text',
        content: JSON.stringify({ markdown: content }),
        status,
        updated_at: new Date().toISOString(),
      };

      if (itemId) {
        const { error } = await supabase
          .from('content_items')
          .update(payload)
          .eq('id', itemId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('content_items')
          .insert({ ...payload, created_at: new Date().toISOString() })
          .select('id')
          .single();
        if (error) throw error;
        if (data) setItemId(data.id);
      }

      toast.success('Media guidelines saved successfully');
    } catch (err) {
      console.error('Error saving media guidelines:', err);
      toast.error('Failed to save media guidelines');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-golf-orange" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="bg-golf-orange/10 p-3 rounded-xl">
          <NewspaperIcon className="w-6 h-6 text-golf-orange" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-golf-gray">Media Guidelines</h2>
          <p className="text-sm text-gray-500">Edit the media guidelines shown on the Press page. Supports Markdown formatting.</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <select
              value={status}
              onChange={e => setStatus(e.target.value as 'Published' | 'Draft')}
              className="rounded-lg border-gray-300 text-sm focus:ring-golf-orange focus:border-golf-orange"
            >
              <option value="Published">Published</option>
              <option value="Draft">Draft</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Content (Markdown)</label>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={20}
              className="w-full rounded-lg border-gray-300 font-mono text-sm focus:ring-golf-orange focus:border-golf-orange"
              placeholder="# Media Guidelines for Ibom Open 2025&#10;&#10;## Accreditation Process&#10;- All media personnel must be accredited..."
            />
          </div>

          <div className="flex justify-end pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2.5 bg-golf-orange text-white rounded-lg font-medium hover:bg-golf-orange/90 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Guidelines'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
